import { Component, Event, EventEmitter, Prop, State } from "@stencil/core";
import { getAuthClient } from "@trumpf/ux.utils";
import authStore from "../../store/authStore";
import { debug } from "../../utils/utils"; // Temporarily remove debug dependency

// Define the type if available, otherwise use HTMLElement
declare global {
    type HTMLUxUSilentAuthElement = HTMLElement;
}

@Component({
    tag: "auth-handler",
    // No shadow DOM needed for this non-visual component
    shadow: false
})
export class AuthHandler {
    /** Unique client name for the auth instance */
    @Prop() readonly clientName!: string;
    /** Auth Client ID from configuration */
    @Prop() readonly clientId!: string;
    /** Auth Issuer URL from configuration */
    @Prop() readonly issuer!: string;
    /** Enable debug logging */
    @Prop() readonly debug: boolean = false;

    /** Emitted when a new authentication token is successfully obtained */
    @Event() tokenReady: EventEmitter<string>;

    @State() private internalTokenState: string | null = null;
    private unsubscribeAuth: () => void | null = null;
    private unsubscribeStore: () => void | null = null;
    // Use the specific type if declared, otherwise fallback to HTMLElement
    private silentAuthElement: HTMLUxUSilentAuthElement | HTMLElement | null = null;
    private silentAuthElementId: string;

    constructor() {
        // Create a unique ID for the silent auth element to avoid conflicts if multiple handlers exist
        this.silentAuthElementId = `silent-auth-${this.clientName}-${Math.random().toString(36).substring(2, 9)}`;
    }

    componentWillLoad() {
        debug.log(this, "AuthHandler: componentWillLoad", { clientName: this.clientName });

        // Check if a silent-auth element for this client already exists
        // Cast to the more specific type if needed, otherwise use HTMLElement
        this.silentAuthElement = document.getElementById(this.silentAuthElementId) as HTMLUxUSilentAuthElement | HTMLElement;

        if (!this.silentAuthElement) {
            debug.log(this, `Creating new silent-auth element with ID: ${this.silentAuthElementId}`);
            this.silentAuthElement = document.createElement("ux-u-silent-auth");
            // Set attributes using the standard HTMLElement interface
            this.silentAuthElement.setAttribute("id", this.silentAuthElementId);
            this.silentAuthElement.setAttribute("client-name", this.clientName);
            this.silentAuthElement.setAttribute("client-id", this.clientId);
            this.silentAuthElement.setAttribute("issuer", this.issuer);
            if (this.debug) {
                this.silentAuthElement.setAttribute("debug", "true");
            }
            document.body.appendChild(this.silentAuthElement);
            debug.log(this, "Silent auth component added to DOM");
        } else {
            debug.log(this, `Reusing existing silent-auth element with ID: ${this.silentAuthElementId}`);
        }

        // Subscribe to auth changes from the client
        const authClient = getAuthClient(this.clientName);
        this.unsubscribeAuth = authClient.subscribe(() => this.handleAuthChange());

        // Subscribe to store changes for debug rendering
        this.unsubscribeStore = authStore.onChange("token", (newToken) => {
            this.internalTokenState = newToken;
            debug.log(
                this,
                "AuthHandler internal state updated from store:",
                this.internalTokenState ? "Token Present" : "No Token"
            );
        });

        // Set initial state from store
        this.internalTokenState = authStore.state.token;

        // Initial check in case the token is already available
        this.handleAuthChange();

        // Force an additional check after a delay
        setTimeout(() => {
            debug.log(this, "Performing delayed auth check");
            this.handleAuthChange();
        }, 1000);
    }

    disconnectedCallback() {
        debug.log(this, "AuthHandler: disconnectedCallback", { clientName: this.clientName });
        if (this.unsubscribeAuth) {
            this.unsubscribeAuth();
            this.unsubscribeAuth = null;
            debug.log(this, "Unsubscribed from AuthClient");
        }
        // Unsubscribe from store
        if (this.unsubscribeStore) {
            this.unsubscribeStore();
            this.unsubscribeStore = null;
        }
        // Remove the silent auth element only if this component instance created it
        const elementToRemove = document.getElementById(this.silentAuthElementId);
        if (elementToRemove && elementToRemove === this.silentAuthElement) {
            document.body.removeChild(elementToRemove);
            debug.log(this, `Removed silent-auth element: ${this.silentAuthElementId}`);
        } else {
            debug.log(this, `Did not remove silent-auth element: ${this.silentAuthElementId} (might be shared or already removed)`);
        }
        this.silentAuthElement = null;
    }

    private handleAuthChange() {
        const currentTokenInStore = authStore.state.token;
        const newToken = getAuthClient(this.clientName).getAccessToken();

        debug.log(this, "AuthHandler: handleAuthChange", {
            newToken: newToken ? "Token Present" : "No Token",
            currentTokenInStore: currentTokenInStore ? "Token Present" : "No Token"
        });

        // If the auth client has a new token that's not in the store yet
        if (newToken && newToken !== currentTokenInStore) {
            debug.log(this, `Token changed. Updating store and emitting tokenReady event.`);
            authStore.setToken(newToken); // Update the store first
            this.tokenReady.emit(newToken); // Emit the event with the new token
        } else if (!newToken && currentTokenInStore) {
            // If the auth client lost the token but the store still has one (e.g., expired)
            debug.log(this, `Auth client has no token but store does. Clearing stale token.`);
            authStore.resetAuthStore(); // Reset the store
            // Optionally emit another event like tokenLost if needed
        } else if (newToken && !currentTokenInStore) {
            // Edge case: Auth client has token, store doesn't (maybe store was reset externally)
            // This can happen if the delayed check runs *after* the auth client got the token
            // but *before* the store subscription updated the state.
            debug.log(this, `Auth client has token, store empty. Updating store and emitting tokenReady.`);
            authStore.setToken(newToken); // Ensure store is updated
            this.tokenReady.emit(newToken);
        }

        // Keep internal state synchronized for rendering debug info
        // Read from store *after* potential updates within this function
        this.internalTokenState = authStore.state.token;
    }

    render() {
        return null;
    }
}
